export interface CategoryView {
  id: number;
  parentId: number | null;
  name: string;
  name_cn?: string | null;
  nameCn?: string | null;
  sortOrder: number;
}

export interface CategoryUpsertRequest {
  parentId: number | null;
  name: string;
  name_cn?: string | null;
  sortOrder: number;
}

export interface FlatCategoryOption {
  id: number;
  parentId: number | null;
  name: string;
  depth: number;
  /** indent-prefixed label, e.g. "└─ 手机数码 (#3)" */
  label: string;
  /** ids on the path from root to this node, including self */
  ancestors: number[];
}

export function categoryDisplayName(category: CategoryView): string {
  return category.name_cn || category.nameCn || category.name;
}

interface InternalNode extends CategoryView {
  children: InternalNode[];
  ancestors: number[];
}

/**
 * Build a flat, indent-prefixed list of categories suitable for the parent picker.
 * Includes a synthetic depth so the caller can disable descendants when editing.
 */
export function flattenForPicker(rows: CategoryView[]): FlatCategoryOption[] {
  const map = new Map<number, InternalNode>();
  for (const row of rows) {
    map.set(row.id, { ...row, children: [], ancestors: [] });
  }
  const roots: InternalNode[] = [];
  for (const row of rows) {
    const node = map.get(row.id)!;
    const parent = row.parentId ? map.get(row.parentId) : undefined;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortRec = (nodes: InternalNode[]) => {
    nodes.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id - b.id);
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);

  const out: FlatCategoryOption[] = [];
  const walk = (nodes: InternalNode[], depth: number, ancestors: number[]) => {
    for (const n of nodes) {
      const path = [...ancestors, n.id];
      const indent = depth === 0 ? '' : `${'\u00A0\u00A0'.repeat(depth - 1)}└─ `;
      out.push({
        id: n.id,
        parentId: n.parentId,
        name: categoryDisplayName(n),
        depth,
        label: `${indent}${categoryDisplayName(n)}  (#${n.id})`,
        ancestors: path,
      });
      walk(n.children, depth + 1, path);
    }
  };
  walk(roots, 0, []);
  return out;
}
